// ── A tenant's brand colour, applied to the whole app ───────────────────────
// Every screen — the office and the crew app — draws its main colour from the
// `--primary` token (index.css). A tenant chooses one colour in Brand
// (orgs.branding.colour); this turns it into `--primary` for light and dark
// mode, so buttons, icons and selected states follow it without any screen
// knowing whose brand it is wearing.
//
// Two rules make an arbitrary colour safe to use:
//   1. Text ON the colour is black or white, whichever has more contrast. The
//      crossover is relative luminance 0.179 — above it black wins, below it
//      white does. (A higher threshold puts white text on mid-tone colours at
//      under 4.5:1, which is unreadable for normal text.)
//   2. Text IN the colour on a white surface (links, tinted buttons) falls back
//      to near-black when the colour is too pale to read — `--primary-text`.
//      In dark mode a dark brand colour is lifted so it still reads on black.

const HEX = /^#[0-9a-f]{6}$/i;

export const isBrandHex = (v) => typeof v === 'string' && HEX.test(v.trim());

const channels = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);

export function luminance(hex) {
  const [r, g, b] = channels(hex).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export const inkOn = (hex) => (luminance(hex) > 0.179 ? '#0F172A' : '#FFFFFF');

function toHsl(hex) {
  const [r, g, b] = channels(hex);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return [h, s * 100, l * 100];
}

function toHex(h, s, l) {
  const sat = s / 100;
  const lig = l / 100;
  const k = (n) => (n + h / 30) % 12;
  const a = sat * Math.min(lig, 1 - lig);
  const f = (n) => lig - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return `#${[f(0), f(8), f(4)].map((x) => Math.round(x * 255).toString(16).padStart(2, '0')).join('')}`;
}

const triple = (h, s, l) => `${h.toFixed(1)} ${s.toFixed(1)}% ${l.toFixed(1)}%`;
const WHITE = '0 0% 100%';
const INK = '222 47% 11%';

// The stylesheet for one brand colour, or '' when there is no valid colour (the
// built-in palette in index.css then stands).
export function brandCss(colour) {
  if (!isBrandHex(colour)) return '';
  const hex = colour.trim();
  const [h, s, l] = toHsl(hex);
  const base = triple(h, s, l);
  const text = luminance(hex) > 0.183 ? INK : base;
  const dl = Math.min(Math.max(l, 55), 72);
  const dark = triple(h, s, dl);
  const darkInk = inkOn(toHex(h, s, dl)) === '#FFFFFF' ? WHITE : INK;
  return [
    `:root{--primary:${base};--primary-foreground:${inkOn(hex) === '#FFFFFF' ? WHITE : INK};--ring:${base};--primary-text:${text};}`,
    `.dark{--primary:${dark};--primary-foreground:${darkInk};--ring:${dark};--primary-text:${dark};}`,
  ].join('\n');
}
